import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AgentAPI } from '../lib/api';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { TopBar } from '../components/TopBar';
import { Dialog } from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { ChevronDown, ChevronRight, Brain, Wrench, FileText, Activity, ScrollText } from 'lucide-react';

type Tab = 'overview' | 'brain' | 'prompts' | 'sessions' | 'logs';

interface MCPTool {
  name: string;
  signature: string;
  doc: string;
  line: number;
}

interface MCPServer {
  name: string;
  file: string;
  fileSize: number;
  description: string;
  tools: MCPTool[];
  logPath: string | null;
  logSize: number | null;
}

interface BrainConfig {
  cwd: string;
  model: string;
  maxTurns: number | null;
  permissionMode: string;
  allowedTools: string[];
  instructionsFile: string;
  instructionsPreview: string;
  instructionsSize: number;
}

interface PromptDef {
  name: string;
  body: string;
  line: number;
}

interface ProjectGroup {
  slug: string;
  name: string;
  path: string;
  servers: MCPServer[];
  brain: BrainConfig | null;
  prompts: PromptDef[] | null;
}

interface AgentsData {
  projects: ProjectGroup[];
  brain: BrainConfig | null;
  servers: MCPServer[];
  prompts: PromptDef[];
  stats: { totalProjects: number; totalServers: number; totalTools: number; totalPrompts: number };
}

function ServerCard({ server, projectSlug, onShowLog }: { server: MCPServer; projectSlug: string; onShowLog: (s: MCPServer, slug: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div className="min-w-0">
            <div className="font-bold text-tg-text">mcp__{server.name}</div>
            {server.description && (
              <div className="text-xs text-tg-text-secondary truncate">{server.description}</div>
            )}
          </div>
        </div>
        <Badge variant="muted">{server.tools.length} tools</Badge>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {server.tools.map((t) => (
            <div key={t.name} className="border-l-2 border-tg-border pl-3 py-1">
              <div className="font-mono text-xs text-tg-accent">{t.signature}</div>
              {t.doc && <div className="text-xs text-tg-text-secondary mt-1 whitespace-pre-wrap">{t.doc}</div>}
            </div>
          ))}
          {server.logPath && (
            <button
              onClick={() => onShowLog(server, projectSlug)}
              className="text-xs text-tg-accent mt-2 underline"
            >
              Логи ({(server.logSize ?? 0) > 1024 ? `${Math.round((server.logSize ?? 0) / 1024)} KB` : `${server.logSize ?? 0} B`})
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

function StageBadge({ stage }: { stage: string }) {
  const map: Record<string, 'success' | 'error' | 'warning' | 'muted'> = {
    completed: 'success',
    failed: 'error',
    error: 'error',
    in_progress: 'warning',
    frames_ready: 'warning',
    video_ready: 'warning',
  };
  return <Badge variant={map[stage] || 'muted'}>{stage}</Badge>;
}

const LOG_SERVER_OPTIONS = [
  { value: 'service', label: 'service (основний)' },
  { value: 'image', label: 'image' },
  { value: 'video', label: 'video' },
  { value: 'recipe_site', label: 'recipe_site' },
  { value: 'whisper', label: 'whisper' },
];

const LINES_OPTIONS = [
  { value: '200', label: '200 рядків' },
  { value: '500', label: '500 рядків' },
  { value: '1000', label: '1000 рядків' },
  { value: '2000', label: '2000 рядків' },
];

const LEVEL_OPTIONS = [
  { value: '', label: 'ALL' },
  { value: 'INFO', label: 'INFO' },
  { value: 'WARN', label: 'WARN' },
  { value: 'ERROR', label: 'ERROR' },
];

const REFRESH_OPTIONS = [
  { value: '0', label: 'Вимкнено' },
  { value: '5000', label: '5 сек' },
  { value: '15000', label: '15 сек' },
  { value: '30000', label: '30 сек' },
];

function LogsTab() {
  const [source, setSource] = useState('service');
  const [lines, setLines] = useState('500');
  const [level, setLevel] = useState('');
  const [refreshInterval, setRefreshInterval] = useState('0');

  const isService = source === 'service';
  const intervalMs = parseInt(refreshInterval) || false;

  const { data: logText, isFetching, dataUpdatedAt } = useQuery<string>({
    queryKey: ['agent-log', source, lines, level],
    queryFn: () => {
      if (isService) {
        return AgentAPI.serviceLog(parseInt(lines), level || undefined) as Promise<string>;
      }
      return AgentAPI.log(source, parseInt(lines)) as Promise<string>;
    },
    refetchInterval: intervalMs as number | false,
    select: (d: any) => (typeof d === 'string' ? d : JSON.stringify(d)),
  });

  const sizeKb = logText ? (new Blob([logText]).size / 1024).toFixed(1) : '0';
  const updatedStr = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('uk-UA')
    : '—';

  async function downloadLog() {
    try {
      let text: any;
      if (isService) {
        text = await AgentAPI.serviceLog(99999, level || undefined);
      } else {
        text = await AgentAPI.log(source, 99999);
      }
      const content = typeof text === 'string' ? text : JSON.stringify(text);
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${source}.log`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(`Помилка: ${e?.message ?? e}`);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Select
          options={LOG_SERVER_OPTIONS}
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="w-44"
        />
        {isService && (
          <Select
            options={LEVEL_OPTIONS}
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="w-28"
          />
        )}
        <Select
          options={LINES_OPTIONS}
          value={lines}
          onChange={(e) => setLines(e.target.value)}
          className="w-36"
        />
        <Select
          options={REFRESH_OPTIONS}
          value={refreshInterval}
          onChange={(e) => setRefreshInterval(e.target.value)}
          className="w-32"
        />
        <Button variant="outline" size="sm" onClick={downloadLog}>
          Завантажити повний лог
        </Button>
      </div>

      <div className="flex items-center gap-3 text-xs text-tg-text-secondary">
        <span className="font-semibold text-tg-text">{source}</span>
        <span>{sizeKb} KB</span>
        <span>оновлено: {updatedStr}</span>
        {isFetching && <span className="text-tg-accent">завантаження...</span>}
      </div>

      <pre
        className="font-mono text-xs text-tg-text whitespace-pre-wrap p-3 rounded-lg overflow-y-auto"
        style={{ background: 'var(--tg-bg)', maxHeight: '60vh', minHeight: '200px' }}
      >
        {logText || 'Лог порожній'}
      </pre>
    </div>
  );
}

function SessionLogModal({
  serverName,
  sessionId,
  onClose,
}: {
  serverName: string;
  sessionId: string;
  onClose: () => void;
}) {
  const shortId = sessionId.slice(0, 8);
  const { data: rawLog, isLoading } = useQuery<string>({
    queryKey: ['agent-log-modal', serverName],
    queryFn: () => AgentAPI.log(serverName, 2000) as Promise<string>,
    select: (d: any) => (typeof d === 'string' ? d : JSON.stringify(d)),
  });

  const filtered = rawLog
    ? rawLog
        .split('\n')
        .filter((line) => line.includes(shortId))
        .join('\n') || '(рядків з цим session_id не знайдено)'
    : '';

  return (
    <Dialog open onClose={onClose} title={`${serverName} / ${shortId}…`}>
      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <pre
          className="font-mono text-xs text-tg-text whitespace-pre-wrap p-2 rounded overflow-y-auto"
          style={{ background: 'var(--tg-bg)', maxHeight: '60vh' }}
        >
          {filtered}
        </pre>
      )}
    </Dialog>
  );
}

function SessionsTab() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sessionLogModal, setSessionLogModal] = useState<{ server: string; sessionId: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['agent-sessions'],
    queryFn: () => AgentAPI.sessions(50),
    refetchInterval: 30000,
  });
  const sessions: any[] = Array.isArray(data) ? data : [];

  if (isLoading) return <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  if (!sessions.length) return <p className="text-sm text-tg-text-secondary">Поки що немає сесій</p>;

  return (
    <>
      <div className="space-y-2">
        {sessions.map((s) => {
          const isOpen = expanded === s.sessionId;
          let ps: any = null;
          if (s.progressState) {
            try { ps = typeof s.progressState === 'string' ? JSON.parse(s.progressState) : s.progressState; } catch {}
          }
          return (
            <Card key={s.sessionId}>
              <button
                className="w-full text-left"
                onClick={() => setExpanded(isOpen ? null : s.sessionId)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <span className="font-mono text-xs text-tg-text-secondary">
                        {s.sessionId.slice(0, 8)}…
                      </span>
                    </div>
                    <div className="text-sm text-tg-text truncate mt-1 pl-5">{s.url}</div>
                    {s.lastError && (
                      <div className="text-xs text-tg-error mt-1 pl-5 truncate">⚠ {s.lastError}</div>
                    )}
                    <div className="text-xs text-tg-text-secondary mt-1 pl-5">
                      {new Date(s.updatedAt * 1000).toLocaleString('uk-UA')}
                    </div>
                  </div>
                  <StageBadge stage={s.stage} />
                </div>
              </button>

              {isOpen && (
                <div className="mt-3 pt-3 border-t border-tg-border space-y-3">
                  {/* Log buttons */}
                  <div className="flex flex-wrap gap-2">
                    {['image', 'video', 'recipe_site'].map((srv) => (
                      <Button
                        key={srv}
                        variant="outline"
                        size="sm"
                        onClick={() => setSessionLogModal({ server: srv, sessionId: s.sessionId })}
                      >
                        {srv === 'image' ? '🔧' : srv === 'video' ? '🎥' : '🌐'} {srv} log
                      </Button>
                    ))}
                  </div>

                  {/* Thumbnail */}
                  {ps?.hosted?.thumbnail_url && (
                    <img
                      src={ps.hosted.thumbnail_url}
                      alt="thumbnail"
                      className="w-20 h-20 rounded object-cover"
                    />
                  )}

                  {/* progress_state */}
                  {ps && (
                    <div>
                      <div className="text-xs font-semibold text-tg-text-secondary mb-1">progress_state</div>
                      {ps.enhanced && (
                        <div className="text-xs text-tg-text space-y-1">
                          {ps.enhanced.banner_path && <div><span className="text-tg-text-secondary">banner: </span>{ps.enhanced.banner_path}</div>}
                          {ps.enhanced.thumbnail_path && <div><span className="text-tg-text-secondary">thumb: </span>{ps.enhanced.thumbnail_path}</div>}
                          {ps.enhanced.step_paths?.length > 0 && (
                            <div><span className="text-tg-text-secondary">steps: </span>{ps.enhanced.step_paths.length} шт.</div>
                          )}
                        </div>
                      )}
                      {ps.hosted && (
                        <div className="text-xs text-tg-text space-y-1 mt-1">
                          {ps.hosted.banner_url && <div><span className="text-tg-text-secondary">banner_url: </span><a href={ps.hosted.banner_url} target="_blank" rel="noopener noreferrer" className="text-tg-accent underline truncate block">{ps.hosted.banner_url}</a></div>}
                          {ps.hosted.video_url && <div><span className="text-tg-text-secondary">video_url: </span><a href={ps.hosted.video_url} target="_blank" rel="noopener noreferrer" className="text-tg-accent underline truncate block">{ps.hosted.video_url}</a></div>}
                          {ps.hosted.step_urls?.length > 0 && (
                            <div><span className="text-tg-text-secondary">step_urls: </span>{ps.hosted.step_urls.length} шт.</div>
                          )}
                        </div>
                      )}
                      {ps.category_slugs?.length > 0 && (
                        <div className="text-xs mt-1"><span className="text-tg-text-secondary">categories: </span>{ps.category_slugs.join(', ')}</div>
                      )}
                    </div>
                  )}

                  {/* All fields */}
                  <details>
                    <summary className="text-xs text-tg-text-secondary cursor-pointer">Всі поля сесії</summary>
                    <pre className="font-mono text-xs text-tg-text whitespace-pre-wrap mt-2 p-2 rounded overflow-x-auto" style={{ background: 'var(--tg-bg)' }}>
                      {JSON.stringify(s, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {sessionLogModal && (
        <SessionLogModal
          serverName={sessionLogModal.server}
          sessionId={sessionLogModal.sessionId}
          onClose={() => setSessionLogModal(null)}
        />
      )}
    </>
  );
}

export function AgentsPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [logServer, setLogServer] = useState<{ server: MCPServer; projectSlug: string } | null>(null);
  const [logContent, setLogContent] = useState<string>('');
  const [logLoading, setLogLoading] = useState(false);

  const { data, isLoading } = useQuery<AgentsData>({
    queryKey: ['agents'],
    queryFn: () => AgentAPI.list() as Promise<AgentsData>,
  });

  async function openLog(server: MCPServer, projectSlug: string) {
    setLogServer({ server, projectSlug });
    setLogLoading(true);
    try {
      const text = await AgentAPI.log(server.name, 300, projectSlug);
      setLogContent(typeof text === 'string' ? text : JSON.stringify(text));
    } catch (e: any) {
      setLogContent(`Помилка завантаження: ${e?.message ?? e}`);
    } finally {
      setLogLoading(false);
    }
  }

  return (
    <div>
      <TopBar title="Агенти" />
      <div className="px-4 md:px-6 py-4 space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !data ? (
          <p className="text-sm text-tg-text-secondary">Немає даних</p>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Підсумок</CardTitle>
              </CardHeader>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div>
                  <div className="text-2xl font-bold text-tg-accent">{data.stats.totalProjects}</div>
                  <div className="text-xs text-tg-text-secondary">Проєкти</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-tg-accent">{data.stats.totalServers}</div>
                  <div className="text-xs text-tg-text-secondary">MCP сервери</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-tg-accent">{data.stats.totalTools}</div>
                  <div className="text-xs text-tg-text-secondary">Інструменти</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-tg-accent">{data.stats.totalPrompts}</div>
                  <div className="text-xs text-tg-text-secondary">Промпти</div>
                </div>
              </div>
            </Card>

            <div className="flex gap-1 border-b border-tg-border overflow-x-auto">
              {([
                ['overview', '🔧 MCP сервери'],
                ['brain', '🧠 Brain'],
                ['prompts', '📝 Промпти'],
                ['sessions', '📊 Сесії'],
                ['logs', '📜 Логи'],
              ] as [Tab, string][]).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className="px-3 py-2 text-sm whitespace-nowrap transition-colors border-b-2"
                  style={{
                    borderColor: tab === key ? 'var(--tg-accent)' : 'transparent',
                    color: tab === key ? 'var(--tg-accent)' : 'var(--tg-text-secondary)',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'overview' && (
              <div className="space-y-4">
                {data.projects.length === 0 ? (
                  <p className="text-sm text-tg-text-secondary">Жодного MCP-сервера не знайдено у зареєстрованих проектах</p>
                ) : (
                  data.projects.map((project) => (
                    <div key={project.slug} className="space-y-2">
                      <div className="flex items-center gap-2 px-1">
                        <h3 className="font-semibold text-sm text-tg-text">{project.name}</h3>
                        <span className="text-xs text-tg-text-secondary font-mono">{project.path}</span>
                        <span className="text-xs text-tg-text-secondary ml-auto">{project.servers.length} MCP</span>
                      </div>
                      {project.servers.map((s) => (
                        <ServerCard key={s.name} server={s} projectSlug={project.slug} onShowLog={openLog} />
                      ))}
                    </div>
                  ))
                )}
              </div>
            )}

            {tab === 'brain' && (
              data.brain ? (
                <div className="space-y-3">
                  <Card>
                    <CardHeader>
                      <CardTitle>Конфігурація</CardTitle>
                    </CardHeader>
                    <dl className="grid grid-cols-2 gap-y-2 text-sm">
                      <dt className="text-tg-text-secondary">Модель</dt>
                      <dd className="text-tg-text font-mono">{data.brain.model}</dd>
                      <dt className="text-tg-text-secondary">Max turns</dt>
                      <dd className="text-tg-text font-mono">{data.brain.maxTurns ?? '—'}</dd>
                      <dt className="text-tg-text-secondary">Permission</dt>
                      <dd className="text-tg-text font-mono">{data.brain.permissionMode}</dd>
                      <dt className="text-tg-text-secondary">Cwd</dt>
                      <dd className="text-tg-text font-mono break-all text-xs">{data.brain.cwd}</dd>
                    </dl>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Allowed tools ({data.brain.allowedTools.length})</CardTitle>
                    </CardHeader>
                    <ul className="space-y-1">
                      {data.brain.allowedTools.map((t) => (
                        <li key={t} className="font-mono text-xs text-tg-text">{t}</li>
                      ))}
                    </ul>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Instructions (CLAUDE.md)</CardTitle>
                      <span className="text-xs text-tg-text-secondary">
                        {data.brain.instructionsSize > 1024
                          ? `${Math.round(data.brain.instructionsSize / 1024)} KB`
                          : `${data.brain.instructionsSize} B`}
                      </span>
                    </CardHeader>
                    <div className="font-mono text-xs text-tg-text whitespace-pre-wrap max-h-96 overflow-y-auto">
                      {data.brain.instructionsPreview}
                      {data.brain.instructionsSize > 600 && '\n…'}
                    </div>
                  </Card>
                </div>
              ) : (
                <p className="text-sm text-tg-text-secondary">Brain не сконфігурований</p>
              )
            )}

            {tab === 'prompts' && (
              <div className="space-y-3">
                {data.prompts.map((p) => (
                  <Card key={p.name}>
                    <CardHeader>
                      <CardTitle>{p.name}</CardTitle>
                      <span className="text-xs text-tg-text-secondary">prompts.py:{p.line}</span>
                    </CardHeader>
                    <div className="font-mono text-xs text-tg-text whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {p.body}
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {tab === 'sessions' && <SessionsTab />}

            {tab === 'logs' && <LogsTab />}
          </>
        )}
      </div>

      <Dialog
        open={!!logServer}
        onClose={() => setLogServer(null)}
        title={`${logServer?.server.name}_server.log`}
      >
        {logLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <pre className="font-mono text-xs text-tg-text whitespace-pre-wrap max-h-96 overflow-y-auto p-2 rounded" style={{ background: 'var(--tg-bg)' }}>
            {logContent || 'Лог порожній'}
          </pre>
        )}
      </Dialog>
    </div>
  );
}
